/**
 * PipelineStageBadge - color-coded badge for a DealPipeline stage.
 */
import React from 'react';
import { PipelineStage } from '../types/dealPipeline';

export const PIPELINE_STAGES: PipelineStage[] = [
  'suggested',
  'reviewed',
  'introduced',
  'meeting_scheduled',
  'due_diligence',
  'term_sheet',
  'negotiating',
  'soft_commitment',
  'closed',
  'passed',
];

interface StageConfig {
  label: string;
  badge: string; // bg + text classes
  dot: string; // dot color
  bg: string; // column bg
  border: string; // column border
  text: string; // column header text color
}

export const STAGE_CONFIG: Record<PipelineStage, StageConfig> = {
  suggested: {
    label: 'Suggested',
    badge: 'bg-gray-100 text-gray-700',
    dot: 'bg-gray-400',
    bg: 'bg-gray-50',
    border: 'border-gray-200',
    text: 'text-gray-700',
  },
  reviewed: {
    label: 'Reviewed',
    badge: 'bg-yellow-100 text-yellow-800',
    dot: 'bg-yellow-400',
    bg: 'bg-yellow-50',
    border: 'border-yellow-200',
    text: 'text-yellow-700',
  },
  introduced: {
    label: 'Introduced',
    badge: 'bg-blue-100 text-blue-800',
    dot: 'bg-blue-500',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    text: 'text-blue-700',
  },
  meeting_scheduled: {
    label: 'Meeting Scheduled',
    badge: 'bg-sky-100 text-sky-800',
    dot: 'bg-sky-500',
    bg: 'bg-sky-50',
    border: 'border-sky-200',
    text: 'text-sky-700',
  },
  due_diligence: {
    label: 'Due Diligence',
    badge: 'bg-indigo-100 text-indigo-800',
    dot: 'bg-indigo-500',
    bg: 'bg-indigo-50',
    border: 'border-indigo-200',
    text: 'text-indigo-700',
  },
  term_sheet: {
    label: 'Term Sheet',
    badge: 'bg-purple-100 text-purple-800',
    dot: 'bg-purple-500',
    bg: 'bg-purple-50',
    border: 'border-purple-200',
    text: 'text-purple-700',
  },
  negotiating: {
    label: 'Negotiating',
    badge: 'bg-fuchsia-100 text-fuchsia-800',
    dot: 'bg-fuchsia-500',
    bg: 'bg-fuchsia-50',
    border: 'border-fuchsia-200',
    text: 'text-fuchsia-700',
  },
  soft_commitment: {
    label: 'Soft Commit',
    badge: 'bg-teal-100 text-teal-800',
    dot: 'bg-teal-500',
    bg: 'bg-teal-50',
    border: 'border-teal-200',
    text: 'text-teal-700',
  },
  closed: {
    label: 'Closed',
    badge: 'bg-green-100 text-green-800',
    dot: 'bg-green-500',
    bg: 'bg-green-50',
    border: 'border-green-200',
    text: 'text-green-700',
  },
  passed: {
    label: 'Passed',
    badge: 'bg-red-100 text-red-800',
    dot: 'bg-red-500',
    bg: 'bg-red-50',
    border: 'border-red-200',
    text: 'text-red-700',
  },
};

interface Props {
  stage: PipelineStage;
  className?: string;
}

const PipelineStageBadge: React.FC<Props> = ({ stage, className = '' }) => {
  const config = STAGE_CONFIG[stage];
  return (
    <span
      className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${config.badge} ${className}`}
    >
      {config.label}
    </span>
  );
};

export default PipelineStageBadge;
